import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isValidGstinChecksum, stateOfGstin } from '@fint/validation';
import { api, ApiError } from '@/lib/api';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { formatDate } from '@/lib/dates';
import { Button, Input, Money, SelectField, Sheet, SheetBody, SheetFooter, SheetForm, SheetHeader, Textarea } from '@/features/common/ui';
import { categoryLabel } from '@/features/quick-add/quick-add';
import { activeOnly } from '@/features/common/Drafts';
import type { InboxItem } from './useInboxCount';
import { changeBody, proposedIds, type ChangeForm } from './inbox-drafts';
import { Note } from './fields';

/**
 * The Inbox's sheets for the catalogue and for a change.
 *
 * ── WHY A DRAFT IS EDITED THROUGH ITS OWN ROUTE ──
 * A draft item IS an item row with `status = 'draft'`, and a draft customer
 * a party row; the only thing a confirm does is flip the status. So fixing
 * the rate the agent misread goes through `PATCH /items/:id` - the same
 * route, the same schema, the same version history - and the Inbox owns no
 * second copy of "what an item is". The sheet reads the row back by id
 * first (`GET /items/:id`), because the Inbox line carries a summary, not
 * every field, and a sheet that opened with the description blank would
 * save it blank.
 *
 * ── WHY THE CHANGE SHEET HOLDS THE PROPOSED IDS, NOT THE RECORD'S ──
 * The change is the thing being edited, not the deposit. The selects open
 * on what Confirm would produce, and Save re-proposes - the deposit itself
 * is untouched until the row is confirmed, which is the whole point of a
 * proposal. A customer the same batch proposed as a DRAFT is not in the
 * active list yet, so the id the change names is kept as its own option,
 * marked "(draft)", rather than silently dropping to "Nobody".
 *
 * Every request goes to the row's OWN book (`orgId`), as the money sheet's
 * do: the Inbox spans the books and the sidebar's book is not the row's.
 */

interface DraftItemRecord {
  id: string;
  name: string;
  type?: 'goods' | 'service';
  rate: string | null;
  unit: string | null;
  description: string | null;
  proposalNote?: string | null;
}

interface DraftCustomerRecord {
  id: string;
  displayName: string;
  companyName: string | null;
  email: string | null;
  phone?: string | null;
  gstin: string | null;
  gstTreatment?: string | null;
  placeOfSupply?: string | null;
}

interface ItemOption {
  id: string;
  name: string;
  status?: string;
}

interface PartyOption {
  id: string;
  displayName: string;
  status?: string;
}

interface CategoryOption {
  id: string;
  name: string;
  emoji?: string | null;
}

/** The customer and item pickers, active rows only - a draft is not offered until somebody confirms it. */
function usePickers(orgId: string | undefined, kind: 'bank_transaction' | 'expense') {
  const customers = useQuery({
    queryKey: ['customers', orgId, 'picker'],
    queryFn: () => api.get<{ items: PartyOption[] }>('/customers?status=active&limit=200', { orgId }).then(activeOnly),
    enabled: !!orgId && kind === 'bank_transaction',
  });
  const items = useQuery({
    queryKey: ['items', orgId, 'picker'],
    queryFn: () => api.get<{ items: ItemOption[] }>('/items?status=active&limit=200', { orgId }).then(activeOnly),
    enabled: !!orgId && kind === 'bank_transaction',
  });
  const vendors = useQuery({
    queryKey: ['vendors', orgId, 'picker'],
    queryFn: () => api.get<{ items: PartyOption[] }>('/vendors?status=active&limit=200', { orgId }).then(activeOnly),
    enabled: !!orgId && kind === 'expense',
  });
  const categories = useQuery({
    queryKey: kind === 'expense' ? ['expense-categories', orgId] : ['income-categories', orgId],
    queryFn: () =>
      api.get<{ items: CategoryOption[] }>(kind === 'expense' ? '/expenses/categories' : '/income-categories', { orgId }),
    enabled: !!orgId,
  });
  return {
    customers: customers.data?.items ?? [],
    items: items.data?.items ?? [],
    vendors: vendors.data?.items ?? [],
    categories: categories.data?.items ?? [],
  };
}

const errorLine = (error: ApiError | undefined) =>
  error && !error.errors?.length ? (
    <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
      {error.message}
    </p>
  ) : null;

// ── A draft item ─────────────────────────────────────────────────────────

export function ItemEditSheet({
  item,
  orgId,
  onClose,
  onSaved,
  onDiscard,
}: {
  item: InboxItem;
  orgId: string | undefined;
  onClose: () => void;
  onSaved: () => void;
  /** Throw this one draft away. See `DiscardDraft`. */
  onDiscard?: () => Promise<unknown>;
}) {
  useEscapeToClose(onClose);
  const record = useQuery({
    queryKey: ['item', orgId, item.id],
    queryFn: () => api.get<DraftItemRecord>(`/items/${item.id}`, { orgId }),
    enabled: !!orgId,
  });
  // The Inbox line's own fields first, so the sheet is usable at once; the
  // record fills in what the line did not carry when it arrives.
  const [form, setForm] = useState({
    name: item.item?.name ?? item.label,
    rate: item.item?.rate ?? '',
    unit: item.item?.unit ?? '',
    description: item.item?.description ?? item.description ?? '',
  });
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (!record.data || touched) return;
    const r = record.data;
    setForm({ name: r.name, rate: r.rate ?? '', unit: r.unit ?? '', description: r.description ?? '' });
  }, [record.data, touched]);

  const save = useMutation({
    mutationFn: () =>
      api.patch(
        `/items/${item.id}`,
        {
          name: form.name,
          rate: form.rate || undefined,
          unit: form.unit || undefined,
          description: form.description || undefined,
        },
        { orgId },
      ),
    onSuccess: onSaved,
  });
  const error = save.error instanceof ApiError ? save.error : undefined;
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTouched(true);
    setForm((f) => ({ ...f, [key]: e.target.value }));
  };

  return (
    <Sheet label="Edit item" onClose={onClose}>
      <SheetHeader title="Edit item" onClose={onClose} />
      <SheetForm
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <SheetBody>
          <p className="text-sm text-muted-foreground">
            A draft from your AI agent. It is in no picker and on no invoice until you confirm it in the Inbox.
          </p>
          {item.proposalNote ? <Note text={item.proposalNote} /> : null}
          <Input label="Name" required autoFocus value={form.name} onChange={set('name')} error={error?.fieldError('name')} />
          <Input label="Rate" inputMode="decimal" value={form.rate} onChange={set('rate')} error={error?.fieldError('rate')} />
          <Input label="Unit" value={form.unit} onChange={set('unit')} hint="hour, month, project, piece…" error={error?.fieldError('unit')} />
          <Textarea label="Description" value={form.description} onChange={set('description')} error={error?.fieldError('description')} />
          {errorLine(error)}
        </SheetBody>
        <SheetFooter>
          <Button type="submit" loading={save.isPending}>
            Save
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {onDiscard ? <DiscardDraft onDiscard={onDiscard} /> : null}
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}

// ── A draft customer ─────────────────────────────────────────────────────

/**
 * The GSTIN's checksum, checked as it is typed - the same rule the Customers
 * page applies on its composer, and the same sentence the server would send
 * back. Said under the field before Save, because an agent reading a GSTIN
 * off a PDF is exactly where a transposed digit comes from.
 */
function gstinProblem(gstin: string): string | undefined {
  if (gstin.length !== 15) return undefined;
  return isValidGstinChecksum(gstin) ? undefined : 'That GSTIN fails its checksum - check for a typo. It prints on every invoice you send them.';
}

export function CustomerEditSheet({
  item,
  orgId,
  onClose,
  onSaved,
  onDiscard,
}: {
  item: InboxItem;
  orgId: string | undefined;
  onClose: () => void;
  onSaved: () => void;
  /** Throw this one draft away. See `DiscardDraft`. */
  onDiscard?: () => Promise<unknown>;
}) {
  useEscapeToClose(onClose);
  const record = useQuery({
    queryKey: ['customer', orgId, item.id],
    queryFn: () => api.get<DraftCustomerRecord>(`/customers/${item.id}`, { orgId }),
    enabled: !!orgId,
  });
  const [form, setForm] = useState({
    displayName: item.customer?.displayName ?? item.label,
    companyName: item.customer?.companyName ?? '',
    email: item.customer?.email ?? '',
    gstin: item.customer?.gstin ?? '',
  });
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (!record.data || touched) return;
    const r = record.data;
    setForm({ displayName: r.displayName, companyName: r.companyName ?? '', email: r.email ?? '', gstin: r.gstin ?? '' });
  }, [record.data, touched]);

  const save = useMutation({
    mutationFn: () => {
      const gstin = form.gstin.trim().toUpperCase();
      const treatment = record.data?.gstTreatment ?? 'consumer';
      return api.patch(
        `/customers/${item.id}`,
        {
          displayName: form.displayName,
          companyName: form.companyName || undefined,
          email: form.email || '',
          gstin: gstin || '',
          // As the Customers page does: a complete, valid GSTIN on a customer
          // still filed as a consumer makes them a registered business, and
          // fills the place of supply from the GSTIN's own state - only while
          // neither has been chosen by hand.
          ...(gstin && treatment === 'consumer' && isValidGstinChecksum(gstin) ? { gstTreatment: 'business_gst' } : {}),
          ...(gstin && !record.data?.placeOfSupply && stateOfGstin(gstin) ? { placeOfSupply: stateOfGstin(gstin)?.code } : {}),
        },
        { orgId },
      );
    },
    onSuccess: onSaved,
  });
  const error = save.error instanceof ApiError ? save.error : undefined;
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setTouched(true);
    setForm((f) => ({ ...f, [key]: key === 'gstin' ? e.target.value.toUpperCase() : e.target.value }));
  };
  const gstinError = error?.fieldError('gstin') ?? gstinProblem(form.gstin);

  return (
    <Sheet label="Edit customer" onClose={onClose}>
      <SheetHeader title="Edit customer" onClose={onClose} />
      <SheetForm
        onSubmit={(e) => {
          e.preventDefault();
          if (gstinProblem(form.gstin)) return;
          save.mutate();
        }}
      >
        <SheetBody>
          <p className="text-sm text-muted-foreground">
            A draft from your AI agent. It is in no picker and on no invoice until you confirm it in the Inbox.
          </p>
          {item.proposalNote ? <Note text={item.proposalNote} /> : null}
          <Input
            label="Display name"
            required
            autoFocus
            value={form.displayName}
            onChange={set('displayName')}
            error={error?.fieldError('displayName')}
            hint="What appears on their invoices and in every picker."
          />
          <Input label="Company name" value={form.companyName} onChange={set('companyName')} error={error?.fieldError('companyName')} />
          <Input label="Email" type="email" value={form.email} onChange={set('email')} error={error?.fieldError('email')} />
          <Input
            label="GSTIN"
            value={form.gstin}
            onChange={set('gstin')}
            error={gstinError}
            hint="Checked against its checksum - a typo here reaches their accountant."
            className="font-mono"
            maxLength={15}
          />
          {errorLine(error)}
        </SheetBody>
        <SheetFooter>
          <Button type="submit" loading={save.isPending}>
            Save
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {onDiscard ? <DiscardDraft onDiscard={onDiscard} /> : null}
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}

// ── A change to a record that exists ─────────────────────────────────────

export function ChangeEditSheet({
  item,
  orgId,
  onClose,
  onSaved,
  onDiscard,
}: {
  item: InboxItem;
  orgId: string | undefined;
  onClose: () => void;
  onSaved: () => void;
  /** Throw this one draft away. See `DiscardDraft`. */
  onDiscard?: () => Promise<unknown>;
}) {
  useEscapeToClose(onClose);
  // A settlement never opens this sheet (see `rowActionFor`); the two
  // filing changes do, and an unknown target reads as a deposit's.
  const targetType = item.target?.type === 'expense' ? 'expense' : 'bank_transaction';
  const pickers = usePickers(orgId, targetType);
  const [form, setForm] = useState<ChangeForm>(() => proposedIds(item.changes));

  // The ids the change already names, kept as options even when the active
  // list does not hold them: a customer proposed as a draft in this very
  // batch, or a list that has not arrived yet.
  const customerOptions = useMemo(() => {
    const listed = pickers.customers;
    if (!form.partyId || listed.some((c) => c.id === form.partyId)) return listed;
    return [...listed, { id: form.partyId, displayName: `${item.changes?.partyName ?? 'Proposed customer'} (draft)` }];
  }, [pickers.customers, form.partyId, item.changes?.partyName]);
  const itemOptions = useMemo(() => {
    const listed = pickers.items;
    if (!form.itemId || listed.some((i) => i.id === form.itemId)) return listed;
    return [...listed, { id: form.itemId, name: `${item.changes?.itemName ?? 'Proposed item'} (draft)` }];
  }, [pickers.items, form.itemId, item.changes?.itemName]);
  const vendorOptions = useMemo(() => {
    const listed = pickers.vendors;
    if (!form.vendorId || listed.some((v) => v.id === form.vendorId)) return listed;
    return [...listed, { id: form.vendorId, displayName: `${item.changes?.vendorName ?? 'Proposed vendor'} (draft)` }];
  }, [pickers.vendors, form.vendorId, item.changes?.vendorName]);
  const categoryOptions = useMemo(() => {
    const listed = pickers.categories;
    if (!form.categoryId || listed.some((c) => c.id === form.categoryId)) return listed;
    return [...listed, { id: form.categoryId, name: item.changes?.categoryName ?? 'Proposed category' }];
  }, [pickers.categories, form.categoryId, item.changes?.categoryName]);

  const save = useMutation({
    mutationFn: async () => {
      const body = changeBody(item.target?.type, item.changes, form);
      // Nothing changed: nothing to send, and nothing to say.
      if (Object.keys(body).length === 0) return;
      await api.patch(`/inbox/changes/${item.id}`, body, { orgId });
    },
    onSuccess: onSaved,
  });
  const error = save.error instanceof ApiError ? save.error : undefined;
  const target = item.target;

  return (
    <Sheet label="Edit change" onClose={onClose}>
      <SheetHeader title="Edit change" onClose={onClose} />
      <SheetForm
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <SheetBody>
          {/* The record this is about, so the pickers are read against it. */}
          {target ? (
            <div className="rounded-md bg-muted px-3 py-2 text-sm" data-testid="inbox-change-record">
              <div className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate">{target.description?.trim() || (targetType === 'expense' ? 'Expense' : 'Deposit')}</span>
                {target.amount ? (
                  <span className="tabular shrink-0 font-medium">
                    <Money value={target.amount} currency={target.currency ?? item.currency} />
                  </span>
                ) : null}
              </div>
              <div className="text-xs text-muted-foreground">
                {target.date ? formatDate(target.date) : ''}
                {target.current?.categoryName ? ` · now ${target.current.categoryName}` : ''}
                {target.current?.partyName ? ` · ${target.current.partyName}` : ''}
              </div>
            </div>
          ) : null}
          {item.proposalNote ? <Note text={item.proposalNote} /> : null}

          {targetType === 'expense' ? (
            <>
              <SelectField
                id="inbox-change-vendor"
                label="Vendor"
                value={form.vendorId}
                onChange={(vendorId) => setForm((f) => ({ ...f, vendorId }))}
                error={error?.fieldError('vendorId')}
              >
                <option value="">Nobody</option>
                {vendorOptions.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.displayName}
                  </option>
                ))}
              </SelectField>
              <SelectField
                id="inbox-change-category"
                label="Category"
                value={form.categoryId}
                onChange={(categoryId) => setForm((f) => ({ ...f, categoryId }))}
                error={error?.fieldError('categoryId')}
              >
                <option value="">Leave as it is</option>
                {categoryOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {categoryLabel(c.name, c.emoji)}
                  </option>
                ))}
              </SelectField>
              {/* What a matched bill adds (0018), shown so the person editing
                  the vendor sees the whole proposal. Not editable here: the
                  change's PATCH takes the filing fields only, and a bill's
                  number is copied off the bill, not typed. */}
              {'referenceNumber' in (item.changes ?? {}) || item.changes?.taxName ? (
                <p className="text-xs text-muted-foreground" data-testid="inbox-change-extras">
                  Also proposed:{' '}
                  {[
                    'referenceNumber' in (item.changes ?? {}) ? `reference ${item.changes?.referenceNumber?.trim() || '—'}` : null,
                    item.changes?.taxName ? item.changes.taxName : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              ) : null}
            </>
          ) : (
            <>
              <SelectField
                id="inbox-change-party"
                label="Paid by"
                value={form.partyId}
                onChange={(partyId) => setForm((f) => ({ ...f, partyId }))}
                error={error?.fieldError('partyId')}
              >
                <option value="">Nobody</option>
                {customerOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.displayName}
                  </option>
                ))}
              </SelectField>
              <SelectField
                id="inbox-change-item"
                label="For"
                value={form.itemId}
                onChange={(itemId) => setForm((f) => ({ ...f, itemId }))}
                error={error?.fieldError('itemId')}
              >
                <option value="">Nothing in particular</option>
                {itemOptions.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </SelectField>
              <SelectField
                id="inbox-change-category"
                label="Income category"
                value={form.categoryId}
                onChange={(categoryId) => setForm((f) => ({ ...f, categoryId }))}
                error={error?.fieldError('incomeCategoryId')}
              >
                <option value="">Leave as it is</option>
                {categoryOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {categoryLabel(c.name, c.emoji)}
                  </option>
                ))}
              </SelectField>
            </>
          )}
          <p className="text-xs text-muted-foreground">
            Save keeps this a proposal. The {targetType === 'expense' ? 'expense' : 'deposit'} itself changes only when you confirm the row.
          </p>
          {errorLine(error)}
        </SheetBody>
        <SheetFooter>
          <Button type="submit" loading={save.isPending}>
            Save
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {onDiscard ? <DiscardDraft onDiscard={onDiscard} /> : null}
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}

/**
 * Throw one draft away, from the sheet that is open on it.
 *
 * ── WHY IT IS HERE AND NOT ONLY IN THE BULK BAR ──
 * The Inbox ticks every row by default - the job is to find what is wrong,
 * not to re-approve what is right - so the only way to delete ONE row was to
 * untick the other ninety-eight, with "Discard 99 selected" a single tap away
 * the whole time. The person looking at a wrongly drafted row in its edit
 * sheet wanted to delete that row, and nothing else.
 *
 * Two steps, because a discard is a real delete: the first tap asks, the
 * second does it. The question says what happens - deleted, never recorded -
 * so "discard" cannot be mistaken for "cancel this edit".
 */
export function DiscardDraft({
  onDiscard,
  question = 'Delete this draft? It is never recorded.',
}: {
  onDiscard: () => Promise<unknown>;
  /** What the second step asks. A correction's record is real; only the proposal goes. */
  question?: string;
}) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!asking) {
    return (
      <Button type="button" variant="ghost" className="ml-auto text-destructive" onClick={() => setAsking(true)} data-testid="draft-discard">
        Discard
      </Button>
    );
  }
  return (
    <span className="ml-auto inline-flex flex-wrap items-center justify-end gap-2" data-testid="draft-discard-ask">
      <span className="text-sm text-muted-foreground">{error ?? question}</span>
      <Button
        type="button"
        variant="destructive"
        size="sm"
        loading={busy}
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            await onDiscard();
          } catch (e) {
            setError(e instanceof Error ? e.message : 'It could not be discarded.');
            setBusy(false);
          }
        }}
        data-testid="draft-discard-confirm"
      >
        Discard
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => setAsking(false)} disabled={busy}>
        Keep
      </Button>
    </span>
  );
}
